import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    getAccessToken,
    getUserId,
    getConnectionId,
} from "../utils/storage";
import { WidgetGrid } from "../components/WidgetGrid";
import { fetchUserConnections } from "../services/databaseService";
import { fetchCollections } from "../services/collectionService";

/**
 * EmbedDashboard – Widget-only view for embedded iframe access.
 *
 * Route: /embed/dashboard
 *
 * This page shows ONLY the user's saved widgets/KPIs for the active connection.
 * No chat bar, no settings, no history, no Ask More, no drag-and-drop.
 * Designed to be rendered inside an iframe from the embed script.
 */
const EmbedDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [connectionName, setConnectionName] = useState<string | null>(null);
    const [collectionId, setCollectionId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    const userId = getUserId();
    const connectionId = getConnectionId();
    const accessToken = getAccessToken();

    useEffect(() => {
        // If no auth data, redirect to login
        if (!accessToken || !userId) {
            navigate("/login", { replace: true });
            return;
        }

        // Load the connection name
        const loadConnectionName = async () => {
            try {
                if (!connectionId) {
                    setConnectionName(null);
                    setLoading(false);
                    return;
                }

                const connections = await fetchUserConnections();
                if (Array.isArray(connections)) {
                    const conn = connections.find(
                        (c: any) => c.id.toString() === connectionId
                    );
                    if (conn) {
                        setConnectionName(
                            conn.connection_name || `Connection ${conn.id}`
                        );
                    }
                }
                const collections = await fetchCollections(parseInt(connectionId));
                setCollectionId(collections[0]?.id || null);
            } catch (error) {
                console.error("[EmbedDashboard] Error loading connection:", error);
            } finally {
                setLoading(false);
            }
        };

        loadConnectionName();
    }, [accessToken, userId, connectionId, navigate]);

    // Loading state
    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingWrapper}>
                    <div style={styles.spinner}></div>
                    <p style={styles.loadingText}>Loading dashboard...</p>
                </div>
            </div>
        );
    }

    // No auth
    if (!userId || !accessToken) {
        return null;
    }

    return (
        <div style={styles.container}>
            {/* Minimal Header — connection name only */}
            <div style={styles.header}>
                <div style={styles.headerContent}>
                    {connectionName && (
                        <div style={styles.connectionBadge}>
                            <div style={styles.connectionDot}></div>
                            <span style={styles.connectionText}>
                                {connectionName}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Widget Grid — read-only */}
            <div style={styles.widgetArea}>
                <WidgetGrid
                    userId={userId}
                    collectionId={collectionId}
                    showAll={true}
                    readOnly={true}
                />
            </div>
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    container: {
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16162a 100%)",
        fontFamily: "Inter, system-ui, -apple-system, sans-serif",
        display: "flex",
        flexDirection: "column",
    },
    header: {
        padding: "16px 24px",
        borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
        background: "linear-gradient(135deg, rgba(26, 26, 46, 0.8), rgba(22, 22, 42, 0.8))",
        backdropFilter: "blur(12px)",
        flexShrink: 0,
    },
    headerContent: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        maxWidth: "1400px",
        margin: "0 auto",
        width: "100%",
    },
    connectionBadge: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "6px 14px",
        borderRadius: "20px",
        background: "rgba(99, 102, 241, 0.1)",
        border: "1px solid rgba(99, 102, 241, 0.2)",
    },
    connectionDot: {
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        background: "#22c55e",
        boxShadow: "0 0 6px rgba(34, 197, 94, 0.5)",
    },
    connectionText: {
        color: "#94a3b8",
        fontSize: "13px",
        fontWeight: 500,
        textTransform: "uppercase" as const,
        letterSpacing: "0.5px",
    },
    widgetArea: {
        flex: 1,
        padding: "24px",
        maxWidth: "1400px",
        margin: "0 auto",
        width: "100%",
    },
    loadingWrapper: {
        display: "flex",
        flexDirection: "column" as const,
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
    },
    spinner: {
        width: "48px",
        height: "48px",
        border: "3px solid rgba(99, 102, 241, 0.2)",
        borderTopColor: "#6366f1",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
        marginBottom: "20px",
    },
    loadingText: {
        color: "#94a3b8",
        fontSize: "15px",
    },
};

// Inject spinner animation globally
const embedDashboardStyle = document.createElement("style");
embedDashboardStyle.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
document.head.appendChild(embedDashboardStyle);

export default EmbedDashboard;
